import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Logo from "../components/Logo";
import { CheckCircle2, Mail } from "lucide-react";
import { useT, useI18n } from "../i18n/I18nContext";

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const { lang } = useI18n();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", password: "", city: "", accept_rules: false,
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [devToken, setDevToken] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState("");

  if (user && verified) return <Navigate to="/app" replace />;

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.accept_rules) { setError(t("auth.err_accept")); return; }
    if (form.password.length < 6) { setError(t("auth.err_pwd_short")); return; }
    setBusy(true);
    const r = await register({ ...form, lang });
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    setRegistered(true);
    setEmailSent(!!r.email_sent);
    if (r.verification_token) setDevToken(r.verification_token);
  };

  const onVerifyDev = async () => {
    setVerifyError("");
    setVerifying(true);
    try {
      await api.post("/auth/verify-email", { token: devToken });
      setVerified(true);
      navigate("/app");
    } catch (e) {
      setVerifyError(e.response?.data?.detail || t("auth.err_verify"));
    } finally {
      setVerifying(false);
    }
  };

  if (registered && !verified) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md card-mabw p-8 md:p-10 anim-fade-up" data-testid="verify-card">
          <div className="w-12 h-12 rounded-full bg-mabw-secondary/15 text-mabw-secondary flex items-center justify-center mb-4">
            {emailSent ? <Mail className="w-6 h-6" /> : <CheckCircle2 className="w-6 h-6" />}
          </div>
          <h1 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>
            {emailSent ? t("auth.check_email_title") : t("auth.verify_title")}
          </h1>
          <p className="text-sm text-mabw-soft mt-2">
            {emailSent ? t("auth.check_email_desc", { email: form.email }) : t("auth.verify_desc")}
          </p>

          {!emailSent && devToken && (
            <>
              <div className="mt-5 bg-stone-50 border border-mabw rounded-xl p-3 text-xs font-mono break-all" data-testid="verify-token">
                {devToken}
              </div>
              {verifyError && (
                <div className="mt-3 text-sm text-red-600" data-testid="verify-error">{verifyError}</div>
              )}
              <button onClick={onVerifyDev} disabled={verifying} className="btn-mabw w-full mt-6" data-testid="verify-confirm-btn">
                {verifying ? t("auth.verifying") : t("auth.verify_btn")}
              </button>
            </>
          )}

          {emailSent && (
            <Link to="/app" className="btn-mabw-secondary mt-6 inline-flex w-full justify-center" data-testid="check-email-enter">
              {t("auth.check_email_continue")}
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-lg card-mabw p-8 md:p-10 anim-fade-up" data-testid="register-card">
        <div className="flex items-center gap-3 mb-2">
          <Logo size={40} />
          <span className="font-semibold" style={{ fontFamily: "Outfit" }}>MABW</span>
        </div>
        <h1 className="text-3xl font-semibold mt-4" style={{ fontFamily: "Outfit" }}>
          {t("auth.register_title")}
        </h1>
        <p className="text-sm text-mabw-soft mt-2">{t("auth.register_sub")}</p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium" htmlFor="r-fn">{t("auth.first_name")}</label>
              <input id="r-fn" required value={form.first_name} onChange={set("first_name")}
                className="input-mabw mt-1.5" data-testid="register-first-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="r-ln">{t("auth.last_name")}</label>
              <input id="r-ln" required value={form.last_name} onChange={set("last_name")}
                className="input-mabw mt-1.5" data-testid="register-last-name-input" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="r-em">{t("auth.email")}</label>
            <input id="r-em" type="email" required value={form.email} onChange={set("email")}
              className="input-mabw mt-1.5" data-testid="register-email-input" />
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="r-pw">{t("auth.password")}</label>
            <input id="r-pw" type="password" required value={form.password} onChange={set("password")}
              className="input-mabw mt-1.5" data-testid="register-password-input" />
            <div className="text-xs text-mabw-soft mt-1">{t("auth.password_hint")}</div>
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="r-ci">{t("auth.city")}</label>
            <input id="r-ci" required value={form.city} onChange={set("city")}
              className="input-mabw mt-1.5" placeholder={t("auth.city_placeholder")} data-testid="register-city-input" />
          </div>
          <label className="flex items-start gap-3 text-sm text-mabw-soft leading-relaxed cursor-pointer">
            <input type="checkbox" checked={form.accept_rules} onChange={set("accept_rules")}
              className="mt-1 accent-current" style={{ accentColor: "var(--mabw-primary)" }} data-testid="register-accept-rules" />
            <span>{t("auth.accept_rules")}</span>
          </label>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2" data-testid="register-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={busy} className="btn-mabw w-full" data-testid="register-submit-btn">
            {busy ? t("auth.creating") : t("auth.create_btn")}
          </button>
        </form>

        <div className="mt-6 text-sm text-mabw-soft">
          {t("auth.have_account")}{" "}
          <Link to="/login" className="text-mabw-primary font-medium" data-testid="register-go-login">
            {t("auth.to_login")}
          </Link>
        </div>
      </div>
    </div>
  );
}
